import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { messagesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Messages.css';

export default function ChatPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = useCallback(async (isInitial) => {
    try {
      const data = await messagesAPI.getMessages(id, { limit: 100 });
      setMessages(data.messages);
      if (isInitial) {
        // Also load conversation to get participants
        const convData = await messagesAPI.getConversations();
        const conv = convData.conversations.find(c => c._id === id);
        if (conv) {
          setOtherUser(conv.participants.find(p => p._id !== user._id));
        }
      }
    } catch { /* ignore */ }
    if (isInitial) setLoading(false);
  }, [id, user._id]);

  useEffect(() => {
    loadMessages(true);
    // Poll for new messages every 5 seconds
    pollRef.current = setInterval(() => loadMessages(false), 5000);
    return () => clearInterval(pollRef.current);
  }, [loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const data = await messagesAPI.sendMessage(id, text.trim());
      setMessages(prev => [...prev, data.message]);
      setText('');
    } catch { /* ignore */ }
    setSending(false);
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="chat-page fade-in">
      <div className="chat-header card">
        <Link to="/messages" className="chat-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
        </Link>
        {otherUser && (
          <Link to={`/profile/${otherUser._id}`} className="chat-user-info">
            <div className="chat-avatar">
              {otherUser.profilePicture ? (
                <img src={otherUser.profilePicture} alt={otherUser.username} />
              ) : (
                <span>{otherUser.username[0].toUpperCase()}</span>
              )}
            </div>
            <span className="chat-username">{otherUser.username}</span>
          </Link>
        )}
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <p>No messages yet. Say hi!</p>
          </div>
        ) : (
          messages.map(msg => {
            const isMine = (msg.sender._id || msg.sender) === user._id;
            return (
              <div key={msg._id} className={`chat-bubble ${isMine ? 'mine' : 'theirs'}`}>
                <p>{msg.text}</p>
                <span className="chat-time">
                  {new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-bar" onSubmit={handleSend}>
        <input
          className="input-field"
          placeholder="Type a message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={500}
          autoFocus
        />
        <button type="submit" className="btn btn-primary" disabled={!text.trim() || sending}>
          Send
        </button>
      </form>
    </div>
  );
}
